import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import * as THREE from "three";

/* ─────────────────────────────────────────
   CONFIGS
───────────────────────────────────────── */
const ANIMALS = {
  hippo: { emoji: "🦛", name: "ฮิปโป", th: "หมูเด้ง", bodyColor: 0xb49cbf, accentColor: 0xe8d5f5, eyeColor: 0x2d1b69, fact: "ฮิปโปวิ่งได้เร็วถึง 30 กม./ชม.!", sound: "😮", hue: "270" },
  panda: { emoji: "🐼", name: "แพนด้า", th: "แพนด้า", bodyColor: 0xffffff, accentColor: 0x333333, eyeColor: 0x111111, fact: "แพนด้ากินไผ่ 12-38 กก./วัน!", sound: "🎋", hue: "120" },
  tiger: { emoji: "🐯", name: "เสือ", th: "เสือ", bodyColor: 0xe8851a, accentColor: 0x1a1a1a, eyeColor: 0x228b22, fact: "เสือว่ายน้ำเก่งมาก ชอบอาบน้ำ!", sound: "🔥", hue: "30" },
  elephant: { emoji: "🐘", name: "ช้าง", th: "ช้าง", bodyColor: 0x8a9ba8, accentColor: 0x607080, eyeColor: 0x3d2b1f, fact: "ช้างจำหน้าคนได้นานหลายปี!", sound: "🐾", hue: "200" },
  penguin: { emoji: "🐧", name: "เพนกวิน", th: "เพนกวิน", bodyColor: 0x1a1a2e, accentColor: 0xffffff, eyeColor: 0xff6b35, fact: "เพนกวินว่ายน้ำได้เร็วสุดๆ!", sound: "❄️", hue: "220" },
  giraffe: { emoji: "🦒", name: "ยีราฟ", th: "ยีราฟ", bodyColor: 0xf5c842, accentColor: 0x8b4513, eyeColor: 0x4a2c0a, fact: "ยีราฟมีลิ้นยาว 45 ซม. สีม่วงเข้ม!", sound: "🌿", hue: "45" },
  lion: { emoji: "🦁", name: "สิงโต", th: "สิงโต", bodyColor: 0xd4a843, accentColor: 0x8b5e3c, eyeColor: 0xc8960c, fact: "สิงโตนอนหลับ 16-20 ชม./วัน!", sound: "👑", hue: "40" },
  otter: { emoji: "🦦", name: "นาก", th: "นาก", bodyColor: 0x8b6914, accentColor: 0xd4a56a, eyeColor: 0x1a0a00, fact: "นากถือมือกันขณะนอนหลับ!", sound: "💧", hue: "35" },
};
const CHATS = [
  { user: "ZooBKK", msg: "น่ารักมากกกก 😍" },
  { user: "PandaFan99", msg: "มาดูทุกวันเลย!" },
  { user: "WildlifeTH", msg: "ช่วงนี้กำลังซุกซน 555" },
  { user: "ผู้ชมสวนสัตว์", msg: "ตาเยิ้มสุดๆ ❤️" },
  { user: "NatureLovers", msg: "โอ้โห มาดูด้วยกัน!" },
  { user: "BabyAnimalClub", msg: "เดี๋ยวกินข้าวหรือยัง?" },
  { user: "AnimalCam", msg: "ถ่ายทอดสดชัดมากวันนี้" },
  { user: "สมาชิกระดับทอง", msg: "ซัพพอร์ต 100 บาท 🎁" },
  { user: "ThaiZoo2025", msg: "น่ารักมากเลย ฮา ❤️" },
  { user: "CuteOverload", msg: "พักผ่อนแบบนี้ถูกใจ~" },
  { user: "AnimalPlanet", msg: "มาดูด้วยครับ 🌍" },
  { user: "ZooKeeper", msg: "วันนี้กินข้าวเยอะมาก!" },
];
const REACTIONS = ["❤️", "😍", "🔥", "😂", "👏", "🎉", "💕", "⭐", "🥰", "🫶"];
const USER_COLORS = ["#f72585", "#7209b7", "#3a86ff", "#06d6a0", "#ffbe0b", "#fb5607", "#4cc9f0", "#80b918"];

/* ─────────────────────────────────────────
   HOOK: screen size
───────────────────────────────────────── */
function useBreakpoint() {
  const [bp, setBp] = useState(() => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1200;
    if (w < 480) return "xs";
    if (w < 768) return "sm";
    if (w < 1024) return "md";
    return "lg";
  });
  useEffect(() => {
    const handler = () => {
      const w = window.innerWidth;
      if (w < 480) setBp("xs");
      else if (w < 768) setBp("sm");
      else if (w < 1024) setBp("md");
      else setBp("lg");
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return bp;
}

/* ─────────────────────────────────────────
   THREE.JS 3D ANIMAL
───────────────────────────────────────── */
function AnimalCanvas({ animalKey }) {
  const mountRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const frameRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const w = mount.clientWidth || 300;
    const h = mount.clientHeight || 200;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100);
    camera.position.set(0, 1.2, 5.5);
    camera.lookAt(0, 0.4, 0);

    scene.add(new THREE.AmbientLight(0xffffff, 0.7));
    const sun = new THREE.DirectionalLight(0xffd6a5, 1.6);
    sun.position.set(4, 8, 4); sun.castShadow = true;
    scene.add(sun);
    const fill = new THREE.DirectionalLight(0xa0d8ef, 0.5);
    fill.position.set(-4, 2, -2); scene.add(fill);
    const rim = new THREE.PointLight(0xff9f7f, 0.7, 20);
    rim.position.set(0, 4, -4); scene.add(rim);

    const ground = new THREE.Mesh(
      new THREE.CylinderGeometry(3, 3, 0.12, 48),
      new THREE.MeshStandardMaterial({ color: 0x78b85a, roughness: 0.95 })
    );
    ground.receiveShadow = true; ground.position.y = -1.05; scene.add(ground);

    const mk = (geo, mat, [x, y, z] = [0, 0, 0], [rx, ry, rz] = [0, 0, 0], [sx, sy, sz] = [1, 1, 1]) => {
      const m = new THREE.Mesh(geo, mat);
      m.position.set(x, y, z); m.rotation.set(rx, ry, rz); m.scale.set(sx, sy, sz);
      m.castShadow = true; return m;
    };

    const cfg = ANIMALS[animalKey] || ANIMALS.hippo;
    const bMat = new THREE.MeshStandardMaterial({ color: cfg.bodyColor, roughness: 0.72 });
    const aMat = new THREE.MeshStandardMaterial({ color: cfg.accentColor, roughness: 0.8 });
    const eMat = new THREE.MeshStandardMaterial({ color: cfg.eyeColor, roughness: 0.2, metalness: 0.3 });
    const wMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.1 });
    const nMat = new THREE.MeshStandardMaterial({ color: cfg.accentColor === 0xffffff ? 0xffbbbb : cfg.accentColor, roughness: 0.65 });

    const group = new THREE.Group();
    const addEye = (parent, x, y, z) => {
      const eg = new THREE.Group();
      eg.add(mk(new THREE.SphereGeometry(0.09, 12, 12), wMat));
      eg.add(mk(new THREE.SphereGeometry(0.06, 12, 12), eMat, [0, 0, 0.04]));
      eg.add(mk(new THREE.SphereGeometry(0.026, 8, 8), wMat, [0.022, 0.022, 0.07]));
      eg.position.set(x, y, z); parent.add(eg);
    };

    if (animalKey === "hippo") {
      group.add(mk(new THREE.SphereGeometry(0.88, 20, 16), bMat, [0, 0, 0], [0, 0, 0], [1.28, 1, 1.1]));
      const head = new THREE.Group();
      head.add(mk(new THREE.SphereGeometry(0.63, 20, 16), bMat, [0, 0, 0], [0, 0, 0], [1.18, 1, 1.08]));
      head.add(mk(new THREE.SphereGeometry(0.36, 14, 12), nMat, [0, -0.14, 0.55], [0, 0, 0], [1.45, 0.7, 1]));
      head.add(mk(new THREE.CylinderGeometry(0.055, 0.055, 0.06, 10), aMat, [-0.11, -0.04, 0.91]));
      head.add(mk(new THREE.CylinderGeometry(0.055, 0.055, 0.06, 10), aMat, [0.11, -0.04, 0.91]));
      addEye(head, -0.24, 0.21, 0.52); addEye(head, 0.24, 0.21, 0.52);
      head.add(mk(new THREE.SphereGeometry(0.17, 12, 12), bMat, [-0.54, 0.42, -0.08], [0, 0, 0], [1, 0.6, 0.6]));
      head.add(mk(new THREE.SphereGeometry(0.17, 12, 12), bMat, [0.54, 0.42, -0.08], [0, 0, 0], [1, 0.6, 0.6]));
      head.position.set(0, 0.6, 0.7); group.add(head);
      [[-0.54, -0.62, 0.37], [0.54, -0.62, 0.37], [-0.52, -0.62, -0.35], [0.52, -0.62, -0.35]].forEach(p =>
        group.add(mk(new THREE.CylinderGeometry(0.19, 0.21, 0.5, 12), bMat, p)));
    } else if (animalKey === "panda") {
      group.add(mk(new THREE.SphereGeometry(0.84, 20, 16), bMat, [0, 0, 0], [0, 0, 0], [1.1, 1, 1]));
      group.add(mk(new THREE.SphereGeometry(0.48, 14, 12), new THREE.MeshStandardMaterial({ color: 0xf0f0f0, roughness: 0.9 }), [0, 0.1, 0.64]));
      const head = new THREE.Group();
      head.add(mk(new THREE.SphereGeometry(0.56, 20, 16), bMat));
      const blk = new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.9 });
      head.add(mk(new THREE.SphereGeometry(0.19, 12, 12), blk, [-0.41, 0.44, -0.04]));
      head.add(mk(new THREE.SphereGeometry(0.19, 12, 12), blk, [0.41, 0.44, -0.04]));
      head.add(mk(new THREE.SphereGeometry(0.16, 12, 12), blk, [-0.2, 0.1, 0.46], [0, 0, 0], [1.3, 1, 0.5]));
      head.add(mk(new THREE.SphereGeometry(0.16, 12, 12), blk, [0.2, 0.1, 0.46], [0, 0, 0], [1.3, 1, 0.5]));
      addEye(head, -0.2, 0.1, 0.56); addEye(head, 0.2, 0.1, 0.56);
      head.add(mk(new THREE.SphereGeometry(0.17, 12, 12), new THREE.MeshStandardMaterial({ color: 0xffd0d0, roughness: 0.7 }), [0, -0.1, 0.51], [0, 0, 0], [1.3, 0.8, 0.7]));
      head.position.set(0, 0.7, 0.58); group.add(head);
      [[-0.5, -0.6, 0.34], [0.5, -0.6, 0.34], [-0.48, -0.6, -0.3], [0.48, -0.6, -0.3]].forEach(p =>
        group.add(mk(new THREE.CylinderGeometry(0.18, 0.2, 0.5, 12), aMat, p)));
    } else if (animalKey === "tiger") {
      group.add(mk(new THREE.SphereGeometry(0.8, 20, 16), bMat, [0, 0, 0], [0, 0, 0], [1.25, 1, 1.05]));
      const strMat = new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.8 });
      for (let i = 0; i < 5; i++) group.add(mk(new THREE.BoxGeometry(0.07, 0.48, 0.85), strMat, [-0.52 + i * 0.26, 0.1, 0], [0, 0, Math.PI * 0.07]));
      const head = new THREE.Group();
      head.add(mk(new THREE.SphereGeometry(0.53, 20, 16), bMat));
      head.add(mk(new THREE.SphereGeometry(0.27, 12, 12), new THREE.MeshStandardMaterial({ color: 0xfde8c8, roughness: 0.8 }), [0, -0.07, 0.43], [0, 0, 0], [1.5, 0.82, 0.72]));
      addEye(head, -0.21, 0.08, 0.47); addEye(head, 0.21, 0.08, 0.47);
      head.add(mk(new THREE.ConeGeometry(0.18, 0.2, 8), bMat, [-0.36, 0.41, 0.1], [0, 0, -0.4]));
      head.add(mk(new THREE.ConeGeometry(0.18, 0.2, 8), bMat, [0.36, 0.41, 0.1], [0, 0, 0.4]));
      head.position.set(0, 0.63, 0.68); group.add(head);
      [[-0.52, -0.6, 0.36], [0.52, -0.6, 0.36], [-0.5, -0.6, -0.3], [0.5, -0.6, -0.3]].forEach(p =>
        group.add(mk(new THREE.CylinderGeometry(0.17, 0.19, 0.5, 12), bMat, p)));
    } else {
      group.add(mk(new THREE.SphereGeometry(0.8, 20, 16), bMat, [0, 0, 0], [0, 0, 0], [1.2, 1, 1]));
      const head = new THREE.Group();
      head.add(mk(new THREE.SphereGeometry(0.55, 20, 16), bMat));
      addEye(head, -0.21, 0.1, 0.49); addEye(head, 0.21, 0.1, 0.49);
      head.add(mk(new THREE.SphereGeometry(0.09, 10, 10), nMat, [0, -0.04, 0.52]));
      head.add(mk(new THREE.SphereGeometry(0.15, 12, 12), aMat, [-0.36, 0.38, -0.04]));
      head.add(mk(new THREE.SphereGeometry(0.15, 12, 12), aMat, [0.36, 0.38, -0.04]));
      head.position.set(0, 0.64, 0.6); group.add(head);
      [[-0.5, -0.6, 0.34], [0.5, -0.6, 0.34], [-0.48, -0.6, -0.3], [0.48, -0.6, -0.3]].forEach(p =>
        group.add(mk(new THREE.CylinderGeometry(0.17, 0.19, 0.5, 12), bMat, p)));
    }

    group.position.y = -0.88; scene.add(group);

    const onMove = (e) => {
      const rect = mount.getBoundingClientRect();
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      mouseRef.current = {
        x: ((cx - rect.left) / rect.width - 0.5) * 2,
        y: ((cy - rect.top) / rect.height - 0.5) * 2,
      };
    };
    mount.addEventListener("mousemove", onMove);
    mount.addEventListener("touchmove", onMove, { passive: true });

    let t = 0;
    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);
      t += 0.016;
      group.position.y = -0.88 + Math.sin(t * 1.2) * 0.06;
      group.scale.y = 1 + Math.sin(t * 1.8) * 0.015;
      group.rotation.y += (mouseRef.current.x * 0.45 - group.rotation.y) * 0.06;
      group.rotation.x += (-mouseRef.current.y * 0.2 - group.rotation.x) * 0.05;
      rim.position.x = Math.sin(t * 0.5) * 3;
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const w2 = mount.clientWidth, h2 = mount.clientHeight;
      camera.aspect = w2 / h2;
      camera.updateProjectionMatrix();
      renderer.setSize(w2, h2);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener("resize", onResize);
      mount.removeEventListener("mousemove", onMove);
      mount.removeEventListener("touchmove", onMove);
      renderer.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
  }, [animalKey]);

  return <div ref={mountRef} style={{ width: "100%", height: "100%", cursor: "grab" }} />;
}

/* ─────────────────────────────────────────
   SUB-COMPONENTS
───────────────────────────────────────── */
function ReactionBurst({ reactions }) {
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 20 }}>
      {reactions.map(r => (
        <div key={r.id} style={{
          position: "absolute", left: `${r.x}%`, bottom: "8%",
          fontSize: 24 + r.sz * 10,
          animation: `riseUp ${1.6 + r.sz}s ease-out forwards`,
          userSelect: "none",
        }}>{r.emoji}</div>
      ))}
    </div>
  );
}

function LiveBadge({ viewers, compact }) {
  const [p, setP] = useState(true);
  useEffect(() => { const t = setInterval(() => setP(v => !v), 850); return () => clearInterval(t); }, []);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: compact ? 6 : 10, flexShrink: 0 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 5,
        padding: compact ? "4px 10px" : "5px 14px",
        background: "linear-gradient(135deg,#e63946,#ff4d6d)",
        borderRadius: 100, fontSize: compact ? 10 : 12, fontWeight: 900, color: "#fff",
        letterSpacing: "0.18em", fontFamily: "'JetBrains Mono',monospace",
        boxShadow: p ? "0 0 18px #e6394699" : "0 0 6px #e6394644",
        transition: "box-shadow 0.85s ease",
      }}>
        <div style={{ width: 6, height: 6, borderRadius: "50%", background: p ? "#fff" : "transparent", transition: "all 0.85s" }} />
        LIVE
      </div>
      {!compact && (
        <div style={{
          display: "flex", alignItems: "center", gap: 5, padding: "5px 12px",
          background: "rgba(0,0,0,0.45)", backdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.1)", borderRadius: 100,
          fontSize: 12, color: "rgba(255,255,255,0.7)", fontFamily: "'JetBrains Mono',monospace",
        }}>
          {/* 👁 {viewers.toLocaleString()} */}
        </div>
      )}
    </div>
  );
}

function ChatMsg({ msg }) {
  const col = USER_COLORS[msg.user.length % USER_COLORS.length];
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", animation: "chatIn 0.35s ease" }}>
      <div style={{
        width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
        fontSize: 10, fontWeight: 900, color: "#fff",
        background: `linear-gradient(135deg,${col},${USER_COLORS[(msg.user.length + 3) % USER_COLORS.length]})`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>{msg.user[0].toUpperCase()}</div>
      <div style={{ flex: 1, fontSize: 12, lineHeight: 1.5 }}>
        <span style={{ fontWeight: 700, color: col, marginRight: 4 }}>{msg.user}</span>
        <span style={{ color: "rgba(255,255,255,0.8)", fontFamily: "'Noto Sans Thai',sans-serif" }}>{msg.msg}</span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
export default function Live() {
  const location = useLocation();
  const { img, url, name } = location.state || {};
  const bp = useBreakpoint();

  const isMobile = bp === "xs" || bp === "sm";  // < 768px
  const isTablet = bp === "md";                  // 768–1023px
  const isDesktop = bp === "lg";                  // ≥ 1024px

  const [sel, setSel] = useState("hippo");
  const [reacts, setReacts] = useState([]);
  const [chat, setChat] = useState(CHATS.slice(0, 4).map((m, i) => ({ ...m, uid: i })));
  const [viewers, setViewers] = useState(8421);
  const [showFact, setShowFact] = useState(false);
  const [tab, setTab] = useState("animals");
  const [chatInput, setChatInput] = useState("");
  const [showPanel, setShowPanel] = useState(false); // mobile slide-up panel
  const chatRef = useRef(null);
  const ridRef = useRef(0);
  const chatIdxRef = useRef(CHATS.length);
  const chatUidRef = useRef(CHATS.slice(0, 4).length);
  const cfg = ANIMALS[sel];
  const accentHue = cfg.hue;

  useEffect(() => {
    const t = setInterval(() => setViewers(v => Math.max(100, v + Math.floor(Math.random() * 7) - 3)), 2800);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const pool = [...CHATS, ...CHATS.map(m => ({ ...m, msg: m.msg + " ✨" }))];
    const t = setInterval(() => {
      const msg = pool[chatIdxRef.current % pool.length];
      setChat(prev => [
        ...prev.slice(-14),
        { ...msg, uid: chatUidRef.current++ },
      ]);
      chatIdxRef.current++;
    }, 2100);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
    });
  }, [chat]);

  const fireReact = useCallback((emoji) => {
    const id = ridRef.current++;
    setReacts(prev => [...prev.slice(-10), { id, emoji, x: 15 + Math.random() * 70, sz: Math.random() }]);
    setTimeout(() => setReacts(prev => prev.filter(r => r.id !== id)), 3000);
  }, []);

  const handleAnimal = (key) => {
    setSel(key);
    setShowFact(true);
    fireReact(ANIMALS[key].sound);
    setTimeout(() => setShowFact(false), 4200);
  };

  const sendChat = () => {
    if (!chatInput.trim()) return;
    setChat(prev => [...prev.slice(-14), { user: "คุณ", msg: chatInput, uid: chatUidRef.current++ }]);
    setChatInput("");
  };

  /* ── PANEL: shared between tablet sidebar & mobile sheet ── */
  const PanelContent = () => (
    <>
      {/* Tabs */}
      <div style={{
        display: "flex", borderRadius: 10, overflow: "hidden",
        border: "1px solid rgba(255,255,255,.07)",
        background: "rgba(255,255,255,.025)", flexShrink: 0,
      }}>
        {[["animals", "🐾 สัตว์"], ["chat", "💬 แชท"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            flex: 1, padding: isMobile ? "10px 0" : "9px 0",
            border: "none", cursor: "pointer",
            fontFamily: "'Noto Sans Thai',sans-serif", fontSize: isMobile ? 14 : 13, fontWeight: 700,
            color: tab === k ? "#fff" : "rgba(255,255,255,.38)",
            background: tab === k
              ? `linear-gradient(135deg,hsl(${accentHue},65%,30%),hsl(${Number(accentHue) + 30},65%,28%))`
              : "transparent",
            transition: "all .22s",
          }}>{label}</button>
        ))}
      </div>

      {/* Animals tab */}
      {tab === "animals" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1, overflow: isMobile ? "visible" : "hidden", minHeight: 0 }}>
          {/* 3D Canvas — hide on xs, show on sm+ */}
          {bp !== "xs" && (
            <div style={{
              height: isMobile ? 160 : 210, borderRadius: 14, overflow: "hidden", flexShrink: 0,
              border: `1px solid hsl(${accentHue},55%,40%,.25)`,
              background: `linear-gradient(135deg,hsl(${accentHue},28%,7%),#0d0d18)`,
              position: "relative", transition: "border-color 1.2s, background 1.2s",
            }}>
              <AnimalCanvas animalKey={sel} />
              <div style={{
                position: "absolute", top: 8, right: 10, zIndex: 5,
                fontSize: 10, color: `hsl(${accentHue},80%,68%)`,
                fontFamily: "'JetBrains Mono',monospace",
                background: "rgba(0,0,0,.55)", padding: "3px 8px", borderRadius: 6,
              }}>3D · แตะได้</div>
              <div style={{
                position: "absolute", bottom: 8, left: 12, zIndex: 5,
                fontFamily: "'Black Han Sans',sans-serif", fontSize: 16,
                color: "#fff", textShadow: "0 2px 8px rgba(0,0,0,.85)",
              }}>{cfg.emoji} {cfg.name}</div>
            </div>
          )}

          {/* Animal grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(4,1fr)" : "repeat(4,1fr)",
            gap: isMobile ? 8 : 7, flexShrink: 0,
          }}>
            {Object.entries(ANIMALS).map(([key, a]) => {
              const active = key === sel;
              return (
                <button key={key} className="animal-btn" onClick={() => handleAnimal(key)} style={{
                  borderRadius: 11, padding: isMobile ? "10px 4px" : "9px 3px",
                  border: active ? `2px solid hsl(${a.hue},70%,58%)` : "2px solid rgba(255,255,255,.07)",
                  background: active
                    ? `linear-gradient(135deg,hsl(${a.hue},50%,17%),hsl(${a.hue},50%,11%))`
                    : "rgba(255,255,255,.03)",
                  cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                  transform: active ? "scale(1.05)" : "scale(1)",
                  boxShadow: active ? `0 4px 18px hsl(${a.hue},70%,45%,.42)` : "none",
                  transition: "all .22s ease",
                }}>
                  <span style={{ fontSize: isMobile ? 24 : 22 }}>{a.emoji}</span>
                  <span style={{ fontSize: isMobile ? 10 : 9, fontWeight: 700, color: active ? `hsl(${a.hue},90%,72%)` : "rgba(255,255,255,.4)" }}>
                    {a.name}
                  </span>
                  {active && <div style={{ width: 14, height: 2, borderRadius: 1, background: `hsl(${a.hue},80%,62%)`, boxShadow: `0 0 6px hsl(${a.hue},80%,62%)` }} />}
                </button>
              );
            })}
          </div>

          {/* Fact card */}
          <div style={{
            padding: "13px 15px", borderRadius: 12,
            background: `linear-gradient(135deg,hsl(${accentHue},40%,9%),rgba(255,255,255,.015))`,
            border: `1px solid hsl(${accentHue},50%,30%,.28)`,
          }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: `hsl(${accentHue},70%,62%)`, letterSpacing: ".12em", marginBottom: 6 }}>DID YOU KNOW?</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,.78)", lineHeight: 1.65 }}>{cfg.fact}</div>
          </div>
        </div>
      )}

      {/* Chat tab */}
      {tab === "chat" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1, minHeight: 0 }}>
          <div ref={chatRef} style={{
            flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10,
            padding: "12px",
            background: "rgba(255,255,255,.02)",
            border: "1px solid rgba(255,255,255,.06)",
            borderRadius: 12,
            height: isMobile ? 200 : undefined,
            minHeight: isMobile ? 200 : 0,
          }}>
            {chat.map((m) => <ChatMsg key={m.uid} msg={m}/>)}

          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <input
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && sendChat()}
              placeholder="พิมพ์ข้อความ..."
              style={{
                flex: 1, padding: "10px 13px", borderRadius: 10,
                border: "1px solid rgba(255,255,255,.1)",
                background: "rgba(255,255,255,.05)", color: "#fff",
                fontFamily: "'Noto Sans Thai',sans-serif", fontSize: 14, outline: "none",
              }}
            />
            <button onClick={sendChat} style={{
              padding: "10px 16px", borderRadius: 10, border: "none",
              background: `linear-gradient(135deg,hsl(${accentHue},70%,42%),hsl(${Number(accentHue) + 30},70%,42%))`,
              color: "#fff", fontSize: 16, cursor: "pointer",
              boxShadow: `0 4px 14px hsl(${accentHue},70%,40%,.42)`,
            }}>➤</button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Black+Han+Sans&family=Noto+Sans+Thai:wght@400;600;800&family=JetBrains+Mono:wght@400;600&display=swap');
        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }

        @keyframes riseUp {
          0%   { transform:translateY(0) scale(1); opacity:1; }
          70%  { opacity:.7; }
          100% { transform:translateY(-220px) scale(.5); opacity:0; }
        }
        @keyframes chatIn {
          from { opacity:0; transform:translateY(8px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes factPop {
          0%  { opacity:0; transform:translateX(-50%) scale(.85); }
          15% { opacity:1; transform:translateX(-50%) scale(1); }
          80% { opacity:1; }
          100%{ opacity:0; transform:translateX(-50%) translateY(-8px); }
        }
        @keyframes gridMove {
          0%   { background-position:0 0; }
          100% { background-position:48px 48px; }
        }
        @keyframes scanMove {
          0%   { transform:translateY(-100%); }
          100% { transform:translateY(110vh); }
        }
        @keyframes slideUp {
          from { transform:translateY(100%); }
          to   { transform:translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity:0; }
          to   { opacity:1; }
        }

        .react-btn { transition:transform .15s, background .15s; }
        .react-btn:hover { transform:scale(1.28)!important; background:rgba(255,255,255,.16)!important; }
        .animal-btn { transition:all .22s ease; }
        .animal-btn:hover { transform:translateY(-2px) scale(1.06)!important; }
        ::-webkit-scrollbar { width:4px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:rgba(255,255,255,.14); border-radius:4px; }
      `}</style>

      <div style={{
        height: "100dvh",
        display: "flex", flexDirection: "column",
        background: "#08080f", color: "#fff",
        fontFamily: "'Noto Sans Thai',sans-serif",
        overflow: "hidden", position: "relative",
      }}>

        {/* BG effects */}
        <div style={{
          position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
          backgroundImage: "linear-gradient(rgba(255,255,255,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.03) 1px,transparent 1px)",
          backgroundSize: "48px 48px",
          animation: "gridMove 8s linear infinite",
        }} />
        <div style={{
          position: "fixed", top: "-15%", left: "25%", width: 600, height: 600,
          borderRadius: "50%", pointerEvents: "none", zIndex: 0,
          background: `radial-gradient(circle, hsl(${accentHue},65%,35%,.12) 0%, transparent 68%)`,
          transition: "background 1.2s ease",
        }} />
        <div style={{ position: "fixed", inset: 0, zIndex: 1, pointerEvents: "none", overflow: "hidden", opacity: .018 }}>
          <div style={{ width: "100%", height: 180, background: "linear-gradient(transparent,rgba(255,255,255,.9),transparent)", animation: "scanMove 10s linear infinite" }} />
        </div>

        {/* ── TOPBAR ── */}
        <div style={{
          position: "relative", zIndex: 10, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: isMobile ? "10px 14px" : "12px 24px",
          background: "rgba(8,8,15,.9)", backdropFilter: "blur(24px)",
          borderBottom: "1px solid rgba(255,255,255,.06)",
          gap: 10,
        }}>
          {/* Left: avatar + title */}
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 14, minWidth: 0 }}>
            <div style={{
              width: isMobile ? 36 : 44, height: isMobile ? 36 : 44,
              borderRadius: "50%", overflow: "hidden", flexShrink: 0,
              border: `2px solid hsl(${accentHue},70%,55%)`,
              boxShadow: `0 0 14px hsl(${accentHue},70%,55%,.5)`,
              background: `linear-gradient(135deg,hsl(${accentHue},70%,30%),hsl(${Number(accentHue) + 40},70%,30%))`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: isMobile ? 18 : 22,
              transition: "border-color .8s, box-shadow .8s",
            }}>
              {img ? <img src={img} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : cfg.emoji}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontFamily: "'Black Han Sans',sans-serif",
                fontSize: isMobile ? "clamp(13px,4vw,17px)" : "clamp(15px,2.4vw,22px)",
                letterSpacing: ".02em",
                background: `linear-gradient(90deg,#fff 0%,hsl(${accentHue},90%,80%) 100%)`,
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {name || cfg.th} Live
              </div>
              {!isMobile && (
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.38)", marginTop: 2, fontFamily: "'JetBrains Mono',monospace" }}>
                  {cfg.emoji} {cfg.name} · สวนสัตว์เปิดเขาเขียว
                </div>
              )}
            </div>
          </div>

          {/* Right: badge + mobile panel toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <LiveBadge viewers={viewers} compact={isMobile} />
            {isMobile && (
              <button
                onClick={() => setShowPanel(v => !v)}
                style={{
                  padding: "7px 12px", borderRadius: 10, border: "none", cursor: "pointer",
                  background: showPanel
                    ? `linear-gradient(135deg,hsl(${accentHue},65%,30%),hsl(${Number(accentHue) + 30},65%,28%))`
                    : "rgba(255,255,255,.08)",
                  color: "#fff", fontSize: 14, fontFamily: "'Noto Sans Thai',sans-serif",
                  transition: "all .22s",
                }}
              >{showPanel ? "✕" : "🐾"}</button>
            )}
          </div>
        </div>

        {/* ── BODY ── */}
        <div style={{
          flex: 1, minHeight: 0, position: "relative", zIndex: 5,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          overflow: isMobile ? "auto" : "hidden",
        }}>

          {/* VIDEO COLUMN */}
          <div style={{
            flex: "1 1 0", display: "flex", flexDirection: "column",
            padding: isMobile ? "12px 12px 0" : isTablet ? "14px 0 14px 14px" : "16px 0 16px 16px",
            gap: isMobile ? 10 : 12,
            minWidth: 0, minHeight: 0,
          }}>

            {/* Video box */}
            <div style={{
              flex: isMobile ? "0 0 auto" : "1 1 0",
              aspectRatio: isMobile ? "16/9" : undefined,
              position: "relative", borderRadius: isMobile ? 12 : 14,
              overflow: "hidden",
              border: `1px solid hsl(${accentHue},55%,40%,.3)`,
              boxShadow: `0 0 30px hsl(${accentHue},70%,40%,.18), inset 0 0 20px hsl(${accentHue},70%,40%,.04)`,
              background: "#000",
              transition: "box-shadow 1.2s ease, border-color 1.2s ease",
            }}>
              <ReactionBurst reactions={reacts} />

              {url ? (
                <iframe src={url} title={`${name} Live`}
                  style={{ width: "100%", height: "100%", border: "none", display: "block" }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen />
              ) : (
                <div style={{
                  width: "100%", height: "100%",
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12,
                  background: `linear-gradient(135deg,hsl(${accentHue},25%,6%),#0d0d18)`,
                  minHeight: isMobile ? 180 : undefined,
                }}>
                  <div style={{ fontSize: isMobile ? 52 : 68, filter: `drop-shadow(0 0 24px hsl(${accentHue},80%,60%))` }}>{cfg.emoji}</div>
                  <div style={{ fontFamily: "'Black Han Sans',sans-serif", fontSize: isMobile ? 15 : 18, color: `hsl(${accentHue},80%,72%)`, letterSpacing: ".04em", textAlign: "center", padding: "0 16px" }}>
                    {cfg.th} กำลัง Live อยู่ตอนนี้!
                  </div>
                </div>
              )}

              {/* Corner brackets */}
              {[[{ top: 0, left: 0 }, { t: true, l: true }, "4px 0 0 0"],
              [{ top: 0, right: 0 }, { t: true, r: true }, "0 4px 0 0"],
              [{ bottom: 0, left: 0 }, { b: true, l: true }, "0 0 0 4px"],
              [{ bottom: 0, right: 0 }, { b: true, r: true }, "0 0 4px 0"]
              ].map(([pos, bd, rad], i) => (
                <div key={i} style={{
                  position: "absolute", width: 16, height: 16, zIndex: 5, ...pos,
                  borderTopWidth: bd.t ? 2 : 0, borderBottomWidth: bd.b ? 2 : 0,
                  borderLeftWidth: bd.l ? 2 : 0, borderRightWidth: bd.r ? 2 : 0,
                  borderStyle: "solid", borderColor: `hsl(${accentHue},80%,65%)`,
                  borderRadius: rad,
                  boxShadow: `0 0 6px hsl(${accentHue},80%,65%,.7)`,
                  transition: "border-color 1.2s",
                }} />
              ))}

              {/* Fact toast */}
              {showFact && (
                <div style={{
                  position: "absolute", bottom: 12, left: "50%",
                  background: "rgba(0,0,0,.92)", backdropFilter: "blur(16px)",
                  border: `1px solid hsl(${accentHue},60%,50%,.4)`,
                  borderRadius: 12, padding: "8px 16px", fontSize: isMobile ? 12 : 13, color: "#fff",
                  whiteSpace: isMobile ? "normal" : "nowrap",
                  maxWidth: isMobile ? "90%" : undefined,
                  textAlign: "center", zIndex: 30,
                  boxShadow: `0 4px 20px hsl(${accentHue},80%,40%,.4)`,
                  animation: "factPop 4.2s ease forwards",
                }}>
                  💡 {cfg.fact}
                </div>
              )}
            </div>

            {/* Reaction bar */}
            <div style={{
              display: "flex", alignItems: "center", gap: isMobile ? 6 : 8, flexWrap: "wrap",
              padding: isMobile ? "8px 10px" : "10px 14px",
              background: "rgba(255,255,255,.03)",
              border: "1px solid rgba(255,255,255,.06)",
              borderRadius: 12, backdropFilter: "blur(12px)", flexShrink: 0,
            }}>
              <span style={{ fontSize: 9, color: "rgba(255,255,255,.3)", marginRight: 2, fontFamily: "'JetBrains Mono',monospace", letterSpacing: ".1em" }}>
                REACT
              </span>
              {REACTIONS.map(r => (
                <button key={r} className="react-btn" onClick={() => fireReact(r)} style={{
                  width: isMobile ? 32 : 36, height: isMobile ? 32 : 36,
                  borderRadius: "50%", border: "none",
                  background: "rgba(255,255,255,.06)", fontSize: isMobile ? 15 : 17, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>{r}</button>
              ))}
            </div>

            {/* ── INLINE PANEL (tablet: below video, before sidebar kicks in) ── */}
            {/* On mobile: handled by slide-up sheet */}
          </div>

          {/* ─── RIGHT SIDEBAR (tablet & desktop only) ─── */}
          {!isMobile && (
            <div style={{
              width: isTablet ? "clamp(240px,34vw,300px)" : "clamp(268px,28vw,340px)",
              flexShrink: 0,
              display: "flex", flexDirection: "column", padding: "16px",
              gap: 12, minHeight: 0,
            }}>
              { PanelContent() }
            </div>
          )}
        </div>

        {/* ── MOBILE SLIDE-UP PANEL ── */}
        {isMobile && showPanel && (
          <>
            {/* Backdrop */}
            <div
              onClick={() => setShowPanel(false)}
              style={{
                position: "fixed", inset: 0, zIndex: 40,
                background: "rgba(0,0,0,.6)", backdropFilter: "blur(4px)",
                animation: "fadeIn .2s ease",
              }}
            />
            {/* Sheet */}
            <div style={{
              position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 50,
              background: "linear-gradient(180deg,#0f0f1e,#08080f)",
              borderTop: "1px solid rgba(255,255,255,.1)",
              borderRadius: "20px 20px 0 0",
              padding: "16px 16px 32px",
              display: "flex", flexDirection: "column", gap: 12,
              maxHeight: "80dvh", overflowY: "auto",
              animation: "slideUp .3s cubic-bezier(.22,1,.36,1)",
            }}>
              {/* Handle bar */}
              <div style={{ width: 40, height: 4, borderRadius: 2, background: "rgba(255,255,255,.2)", margin: "0 auto 4px" }} />
              { PanelContent() }
            </div>
          </>
        )}
      </div>
    </>
  );
}